/**
 * dmlJoinSources — tables an UPDATE / DELETE reads that its QueryStructure
 * does not carry. MySQL's multi-table `UPDATE a JOIN b ON … SET …` (and the
 * `UPDATE a, b SET …` / `DELETE a FROM a, b` comma forms) keep no trace of
 * `b` in the structure (PostgreSQL's `UPDATE … FROM b` and SQL Server's
 * `UPDATE … FROM a JOIN b` do), so the column pickers — which offer only the
 * statement's own tables — would never list `b`. The statement's relation
 * lists are read from its text instead (`dmlRelationLists`: never a SET
 * assignment, OUTPUT or RETURNING item); a table under an alias is qualified
 * by that alias. Applied on every engine; only MySQL's shapes need it today.
 * `statementSources` reads a statement's own sources that way over any text
 * span — the statement being edited, or an UPDATE / DELETE enclosing a
 * drilled subquery (`outerQuerySources`).
 */

import type { ParseOptions, SqlEngine } from '../../../../sql-grammar/model';
import { dmlRelationLists, type ListedRelation } from '../../../../sql-resolver/scope/dmlRelationLists';
import { foldFor } from '../../../../sql-resolver/service/schemaProjectionHelpers';
import { aliasSql, boundLeafSql } from '../../../../sql-resolver/suggest/sqlIdentifierText';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import type { SchemaSnapshot } from '../../../../sql-resolver/types/schema';
import { bindingKeyOf, declaredCteKeys, querySources, type QuerySource } from '../../../../sql-resolver/scope/querySources';

type TextSpan = { readonly source: string; readonly start: number; readonly end: number; readonly options: ParseOptions };

/**
 * A statement's own sources: the relations its structure carries, then (an
 * UPDATE / DELETE) the relation-list tables it does not — read from `span`.
 */
export function statementSources(
  span: TextSpan | null,
  structure: QueryStructure | null,
  engine: SqlEngine,
  snapshot?: SchemaSnapshot | null,
): QuerySource[] {
  const listed = querySources(structure, engine, { withDmlTarget: true, snapshot });
  return span && structure ? [...listed, ...listedDmlSources(span, structure, engine, listed)] : listed;
}

/**
 * An UPDATE / DELETE's text `span` — its relation lists' tables that `known`
 * does not already hold (only those `include` accepts, when given).
 */
export function listedDmlSources(
  span: TextSpan,
  structure: QueryStructure,
  engine: SqlEngine,
  known: readonly QuerySource[],
  include: (relation: ListedRelation) => boolean = () => true,
): QuerySource[] {
  if (structure.kind !== 'update' && structure.kind !== 'delete') return [];
  const fold = foldFor(engine);
  const have = new Set(known.flatMap((s) => (s.relationKey ? [s.relationKey] : [])));
  const ctes = declaredCteKeys(structure, engine);
  const out: QuerySource[] = [];
  for (const relation of dmlRelationLists(span.source, span.start, span.end, structure.kind, span.options)) {
    if (!include(relation)) continue;
    const { qualified: qn, alias } = relation;
    const key = fold(qn.name);
    if (have.has(key) || ctes.has(key)) continue;
    have.add(key);
    out.push({
      kind: 'relation',
      exposedName: alias?.text ?? qn.name,
      qualifierSql: alias ? aliasSql(alias.text, alias.folded, engine) : boundLeafSql(qn, engine),
      bindingKey: bindingKeyOf(engine, alias?.folded ?? qn.foldedName),
      relationKey: key,
      ...(qn.schema ? { schemaKey: fold(qn.schema) } : {}),
      ...(qn.database ? { databaseKey: fold(qn.database) } : {}),
    });
  }
  return out;
}
